import { useLoaderData, useRevalidator, useRouteError } from "react-router-dom";
import GenericErrorDisplay, { useSetError } from "../generic-error";
import useAuth, { useRefreshAuth } from "../../auth/hooks";
import { useState } from "react";
import TextInput from "../../components/inputs/text";
import { CircularProgress, IconButton, InputAdornment } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faEye,
  faEyeSlash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import DropdownInput from "../../components/inputs/radio/dropdown";
import { PublicLc } from "../../dtos/lc";
import LcService from "../../service/lc";
import SwitchInput from "../../components/inputs/checkbox/switch";
import NumberInput from "../../components/inputs/number";
import FileInput from "../../components/inputs/file";
import UserService from "../../service/user";
import { getUserPrivateId } from "../../dtos/user";

export const path = "user/";

type LoaderType = [PublicLc[]];

export async function loader(): Promise<LoaderType> {
  const lcs = new LcService().read.all();
  return [await lcs];
}

export function Component() {
  const auth = useAuth();
  const [lcs] = useLoaderData() as LoaderType;

  const refreshAuth = useRefreshAuth();

  const [loading, setLoading] = useState<boolean>(false);
  const revalidator = useRevalidator();
  const setErrorOnly = useSetError();
  const setError = (error: unknown) => {
    setErrorOnly(error);
    revalidator.revalidate();
    refreshAuth();
  };

  const [passwordShown, setPasswordShown] = useState<boolean>(false);

  const [publicId, setPublicId] = useState<string>(auth?.public_id ?? "");
  const [email, setEmail] = useState<string>(auth?.email ?? "");
  const [password, setPassword] = useState<string>("");
  const [passwordConfirmation, setPasswordConfirmation] = useState<string>("");
  const [firstName, setFirstName] = useState<string>(auth?.first_name ?? "");
  const [lastName, setLastName] = useState<string>(auth?.last_name ?? "");
  const [lcId, setLcId] = useState<string>(auth?.lc.id ?? "");
  const [year, setYear] = useState<number | null>(auth?.year ?? null);
  const [medicine, setMedicine] = useState<boolean>(auth?.is_medicine ?? true);
  const [programmeTitle, setProgrammeTitle] = useState<string>(
    auth?.programme_title ?? "",
  );
  const [series, setSeries] = useState<number | null>(auth?.series ?? null);
  const [group, setGroup] = useState<number | null>(auth?.group ?? null);
  const [registrationNumber, setRegistrationNumber] = useState<string>(
    auth?.registration_number ?? "",
  );
  const [idImage, setIdImage] = useState<File | null>(null);

  const [otp, setOtp] = useState<string>("");
  const [passwordCurrent, setPasswordCurrent] = useState<string>("");

  const [otpAction, setOtpAction] = useState<string | null>(null);

  return auth === null ? (
    <h1>
      Neconectat <CircularProgress />
    </h1>
  ) : (
    <>
      <h4>Bine ai venit,</h4>
      <h1>
        {auth.first_name} {auth.last_name}
      </h1>
      <div
        className="surface"
        style={{
          backgroundColor: auth.valid ? "green" : "yellow",
          color: auth.valid ? "white" : "var(--color-dark)",
        }}
      >
        <b>{auth.valid ? "Cont valid" : "Așteaptă validarea"}</b>
        {": "}
        {auth.valid
          ? "Contul tău a fost validat de către centrul local. Te invităm să descoperi oportunitățile puse la dispoziție de FASMR."
          : "Contul tău încă nu a fost validat de către validatorii centrului tău local. Pentru a te putea înscrie la oportunitățile FASMR trebuie să ai un cont valid."}
      </div>
      {auth.validation_message !== null && (
        <form
          onSubmit={async (event) => {
            event.preventDefault();

            setError("");
            setLoading(true);
            try {
              await new UserService().delete.validationProblems(auth.id);
            } catch (error) {
              setError(error);
            } finally {
              await refreshAuth();
              setLoading(false);
              revalidator.revalidate();
            }
          }}
          className="surface"
          style={{
            backgroundColor: "red",
            color: "white",
            display: "flex",
            alignItems: "center",
            gap: "var(--gap-small)",
          }}
        >
          <FontAwesomeIcon
            icon={faTriangleExclamation}
            size="2xl"
            style={{ margin: "var(--gap-small)" }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: "var(--gap-small)",
            }}
          >
            <div className="label">
              Validatorii au descoperit probleme în legătură cu datele contului
              tău:
            </div>
            <div>{auth.validation_message}</div>
            <div className="label">
              Revolvă problemele, apoi apasă pe butonul de mai jos.
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "var(--gap-small)",
              }}
            >
              <button
                className="secondary button outlined"
                style={{ margin: 0, ...{ "--color": "white" } }}
                type="submit"
              >
                <FontAwesomeIcon icon={faCheck} /> Semnalează rezolvarea
                problemelor
              </button>
              {(loading || revalidator.state === "loading") && (
                <CircularProgress sx={{ color: "white" }} />
              )}
            </div>
          </div>
        </form>
      )}
      <div className="surface" style={{ wordBreak: "break-all" }}>
        <h3 style={{ marginBottom: "var(--gap-medium)" }}>
          Identificatori (id-uri)
        </h3>
        <div>
          Identificator public: <b>{auth.public_id}</b>
        </div>
        <div>
          Identificator privat: <b>{getUserPrivateId(auth)}</b>
        </div>
        <div className="label" style={{ marginTop: "var(--gap-medium)" }}>
          <b>Important!</b> Pentru a putea asigura confidențialitatea te rugăm
          să nu faci public identificatorul privat. Acesta este folosit{" "}
          <b>exclusiv</b> pentru identificarea anonimă în liste și nu va fi
          solicitat ca și dată de intrare de către site, astfel că staff-ul nu
          ți-l va cere niciodată.
          <br />
          <b>Notă!</b> Modificarea id-ului public atrage după sine modificarea
          automată a id-ului privat.
        </div>
      </div>
      <div>
        Modifică informațiile contului tău. Modificarea datelor personale
        necesită revalidarea contului.
      </div>
      <form
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "var(--gap-small)",
        }}
        onSubmit={async (event) => {
          event.preventDefault();
          setError("");
          setLoading(true);
          try {
            if (otpAction !== null) {
              await new UserService().create.finishOtp(otpAction, otp.trim());
              setOtpAction(null);
            } else {
              if (password.length !== 0 && password !== passwordConfirmation)
                throw new Error("Parolele nu coincid.");

              setOtpAction(
                await new UserService().update.user(
                  auth.id,
                  passwordCurrent.trim(),
                  email.trim(),
                  publicId.trim(),
                  password.trim().length > 0
                    ? password.trim()
                    : passwordCurrent.trim(),
                  firstName.trim(),
                  lastName.trim(),
                  lcId.trim(),
                  year!,
                  medicine,
                  medicine ? "Medicină" : programmeTitle.trim(),
                  series!,
                  group!,
                  registrationNumber.trim(),
                  idImage ?? undefined,
                ),
              );
            }
          } catch (error) {
            setError(error);
          } finally {
            refreshAuth();
            setLoading(false);
            setPassword("");
            setPasswordConfirmation("");
            setPasswordCurrent("");
            revalidator.revalidate();
          }
        }}
      >
        <TextInput
          required
          label="Email"
          onChange={(event) => setEmail(event.target.value)}
          value={email}
          disabled={otpAction !== null}
        />
        <TextInput
          required
          label={"Id public"}
          onChange={(event) => setPublicId(event.target.value)}
          value={publicId}
          disabled={otpAction !== null}
        />
        <TextInput
          required
          label="Prenume"
          onChange={(event) => setFirstName(event.target.value)}
          value={firstName}
          disabled={otpAction !== null}
        />
        <TextInput
          required
          label="Nume de familie"
          onChange={(event) => setLastName(event.target.value)}
          value={lastName}
          disabled={otpAction !== null}
        />
        <DropdownInput
          label="Centru"
          options={Object.fromEntries(lcs.map((lc) => [lc.id, lc.name_short]))}
          value={lcId}
          onChange={(lc) => setLcId(lc)}
          disabled={otpAction !== null}
        />
        <SwitchInput
          label="Student la medicină"
          checked={medicine}
          onChange={(e) => setMedicine(e.target.checked)}
          disabled={otpAction !== null}
        />
        {!medicine && (
          <TextInput
            required
            label="Program de studiu"
            onChange={(event) => setProgrammeTitle(event.target.value)}
            value={programmeTitle}
            disabled={otpAction !== null}
          />
        )}
        <NumberInput
          required
          label="An de studiu"
          onChange={(e) =>
            setYear(
              isNaN(parseInt(e.target.value)) ? null : parseInt(e.target.value),
            )
          }
          value={year ?? ""}
          disabled={otpAction !== null}
        />
        <NumberInput
          required
          label="Serie"
          helperText="Dacă este o singură serie, atunci aceasta este seria 1"
          onChange={(e) =>
            setSeries(
              isNaN(parseInt(e.target.value)) ? null : parseInt(e.target.value),
            )
          }
          value={series ?? ""}
          disabled={otpAction !== null}
        />
        <NumberInput
          required
          label="Grupă"
          helperText="Dacă este o singură grupă, atunci aceasta este grupa 1"
          onChange={(e) =>
            setGroup(
              isNaN(parseInt(e.target.value)) ? null : parseInt(e.target.value),
            )
          }
          value={group ?? ""}
          disabled={otpAction !== null}
        />
        <TextInput
          required
          label="Număr de înmatriculare"
          onChange={(event) => setRegistrationNumber(event.target.value)}
          value={registrationNumber}
          disabled={otpAction !== null}
        />
        <FileInput
          label="Fotografie carnet student"
          onChange={(files) =>
            setIdImage(files.length > 0 ? (files[0] ?? null) : null)
          }
          types={["png", "jpg", "jpeg"]}
          multiple={false}
          disabled={otpAction !== null}
          remoteValue={auth.id_image}
        />
        <TextInput
          type={passwordShown ? "" : "password"}
          label="Parolă nouă"
          onChange={(event) => setPassword(event.target.value)}
          value={password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setPasswordShown((value) => !value)}>
                  <FontAwesomeIcon
                    icon={passwordShown ? faEyeSlash : faEye}
                    size="xs"
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          disabled={otpAction !== null}
        />
        <TextInput
          type={passwordShown ? "" : "password"}
          label="Confirmă parola nouă"
          onChange={(event) => setPasswordConfirmation(event.target.value)}
          value={passwordConfirmation}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setPasswordShown((value) => !value)}>
                  <FontAwesomeIcon
                    icon={passwordShown ? faEyeSlash : faEye}
                    size="xs"
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          disabled={otpAction !== null}
        />
        <div
          style={{
            height: "var(--gap-big)",
          }}
        />
        <hr />
        <TextInput
          required
          type={passwordShown ? "" : "password"}
          label="Parola actuală"
          onChange={(event) => setPasswordCurrent(event.target.value)}
          value={passwordCurrent}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setPasswordShown((value) => !value)}>
                  <FontAwesomeIcon
                    icon={passwordShown ? faEyeSlash : faEye}
                    size="xs"
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
          disabled={otpAction !== null}
        />
        {otpAction !== null && (
          <TextInput
            required
            label="Cod de verificare"
            helperText={"Trimis la " + email}
            onChange={(event) => setOtp(event.target.value)}
            value={otp}
          />
        )}
        <div
          style={{
            alignSelf: "flex-start",
            display: "flex",
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "stretch",
            gap: "var(--gap-small)",
            color: "var(--color-fasmr-blue)",
          }}
        >
          <button type="submit" className="primary button">
            {otpAction !== null ? "Verifică" : "Salvează datele"}
          </button>
          {otpAction !== null && (
            <>
              <button
                type="button"
                className="secondary button"
                onClick={() => setOtpAction(null)}
              >
                Modifică
              </button>
              <button
                type="button"
                className="primary button text"
                onClick={async (event) => {
                  event.preventDefault();
                  setError("");
                  setLoading(true);
                  try {
                    await new UserService().read.newOtp(otpAction);
                  } catch (error) {
                    setError(error);
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                Cod nou
              </button>
            </>
          )}
          {(loading || revalidator.state === "loading") && <CircularProgress />}
        </div>
      </form>
    </>
  );
}
Component.displayName = "page-account-user";

export function ErrorBoundary() {
  const error = useRouteError();
  return <GenericErrorDisplay error={error} />;
}
ErrorBoundary.displayName = "page-account-user-error";
